/**
 * Browser notification helper for Chatwoot.
 * Shows native browser notifications for new incoming messages and mentions.
 * Activated per-account via Features settings and filtered by custom role.
 */

const ICON_URL = '/android-icon-192x192.png';
const BADGE_URL = '/favicon-badge-32x32.png';

const getConversationURL = (accountId, conversationId) =>
  `${window.location.origin}/app/accounts/${accountId}/conversations/${conversationId}`;

/**
 * Returns true when we should NOT show the in-tab notification because the
 * agent is actively looking at THIS conversation. We still notify when the
 * tab is visible but on another conversation, otherwise agents would never
 * be alerted of new messages while they're focused on a different chat.
 */
const isAgentLookingAtConversation = conversationId => {
  if (document.visibilityState !== 'visible') return false;
  // Match the URL pattern /accounts/:id/conversations/:conv_id with conversationId
  const match = window.location.pathname.match(/\/conversations\/(\d+)/);
  if (!match) return false;
  return Number(match[1]) === Number(conversationId);
};

const show = ({ title, body, conversationId, accountId }) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('[notify] Notification API unavailable');
    return;
  }
  if (Notification.permission !== 'granted') {
    // eslint-disable-next-line no-console
    console.warn('[notify] permission not granted:', Notification.permission);
    return;
  }
  // Skip only when the agent has THIS exact conversation open and visible.
  // If the tab is in background OR a different conversation is open, alert.
  if (isAgentLookingAtConversation(conversationId)) {
    // eslint-disable-next-line no-console
    console.log(
      '[notify] agent is looking at conv #%s, skipping',
      conversationId
    );
    return;
  }

  const notification = new Notification(title, {
    body,
    icon: ICON_URL,
    badge: BADGE_URL,
    tag: `cw-conv-${conversationId}`,
    renotify: true,
    requireInteraction: false,
  });

  notification.onclick = () => {
    window.focus();
    window.location.href = getConversationURL(accountId, conversationId);
    notification.close();
  };
};

/**
 * Returns true if browser notifications are enabled for the current user.
 * Reads from account settings (browser_notifications_enabled + browser_notifications_roles).
 */
export const isBrowserNotificationsEnabledForUser = store => {
  const accountId = store.getters.getCurrentAccountId;
  const account = store.getters['accounts/getAccount']?.(accountId) || {};
  const settings = account.settings || {};

  if (!settings.browser_notifications_enabled) return false;

  const allowedRoles = settings.browser_notifications_roles;
  if (!allowedRoles || !Array.isArray(allowedRoles) || !allowedRoles.length)
    return true;

  const customRoleId = store.getters.getCurrentCustomRoleId;
  const userRole = store.getters.getCurrentRole;

  if (customRoleId) {
    return (
      allowedRoles.includes(String(customRoleId)) ||
      allowedRoles.includes(customRoleId)
    );
  }
  return allowedRoles.includes(userRole);
};

/**
 * Request browser notification permission unconditionally.
 * Used when the admin explicitly enables the feature from settings.
 */
export const askBrowserNotificationPermission = () => {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission();
  }
};

/**
 * Request browser notification permission on startup if notifications are enabled for this user.
 */
export const requestBrowserNotificationPermission = store => {
  if (!isBrowserNotificationsEnabledForUser(store)) return;
  askBrowserNotificationPermission();
};

/**
 * Called from actionCable onMessageCreated.
 * Only shows notification for incoming messages (type 0) on conversations
 * assigned to the current user (or unassigned).
 */
export const notifyNewMessage = (store, data) => {
  if (!isBrowserNotificationsEnabledForUser(store)) {
    // eslint-disable-next-line no-console
    console.log('[notify] feature disabled for this user');
    return;
  }
  // Only incoming messages (type 0); skip outgoing (1), private notes (2), etc.
  if (data.message_type !== 0) return;

  const accountId = store.getters.getCurrentAccountId;
  const currentUserId = store.getters.getCurrentUserID;
  const conversationId = data.conversation_id;

  // The message.created event sends conversation.assignee_id at the top level.
  // Fall back to the store cache for cases where the WS payload omits it.
  const fromPayload = data.conversation?.assignee_id;
  const fromStore =
    store.getters.getConversationById(conversationId)?.meta?.assignee?.id;
  const assigneeId = fromPayload ?? fromStore;

  // Only notify if the conversation is assigned to the current user.
  // Unassigned conversations or those assigned to someone else are skipped —
  // those agents will receive their own notification (or via Web Push).
  if (!assigneeId) {
    // eslint-disable-next-line no-console
    console.log(
      '[notify] conversation #%s has no assignee, skipping in-app notify',
      conversationId
    );
    return;
  }
  if (assigneeId !== currentUserId) {
    // eslint-disable-next-line no-console
    console.log(
      '[notify] conversation #%s assigned to %s (not me=%s), skipping',
      conversationId,
      assigneeId,
      currentUserId
    );
    return;
  }

  const senderName = data.sender?.name || 'Contacto';
  // Truncate long messages for the notification body
  const rawContent = data.content || '';
  const messagePreview =
    rawContent.length > 100
      ? `${rawContent.slice(0, 97)}...`
      : rawContent || '📎 Archivo adjunto';

  show({
    title: `💬 ${senderName}`,
    body: messagePreview,
    conversationId,
    accountId,
  });
};

/**
 * Called from actionCable onNotificationCreated.
 * Only shows notification for 'mention' type notifications.
 */
export const notifyMention = (store, data) => {
  if (!isBrowserNotificationsEnabledForUser(store)) return;

  const notification = data.notification || data;
  if (notification.notification_type !== 'mention') return;

  const accountId = store.getters.getCurrentAccountId;
  const conversationId =
    notification.primary_actor?.id || notification.primary_actor_id;
  if (!conversationId) return;

  let mentionBody = `Conversación #${conversationId}`;
  if (notification.content) {
    mentionBody =
      notification.content.length > 100
        ? `${notification.content.slice(0, 97)}...`
        : notification.content;
  }

  show({
    title: '🔔 Te han mencionado',
    body: mentionBody,
    conversationId,
    accountId,
  });
};

/**
 * Shows a single notification immediately, bypassing the "agent is looking at
 * this conversation" guard. Used by the Features test buttons so the admin can
 * confirm notifications actually fire on their machine.
 * Returns true if the notification was shown, false otherwise (no permission / API).
 */
export const showTestNotification = ({
  title,
  body,
  conversationId = null,
  accountId = null,
}) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('[notify-test] Notification API unavailable');
    return false;
  }
  if (Notification.permission !== 'granted') {
    // eslint-disable-next-line no-console
    console.warn(
      '[notify-test] permission not granted:',
      Notification.permission
    );
    return false;
  }

  const notification = new Notification(title, {
    body,
    icon: ICON_URL,
    badge: BADGE_URL,
    tag: conversationId ? `cw-conv-${conversationId}` : `cw-test-${Date.now()}`,
    renotify: true,
    requireInteraction: false,
  });

  if (conversationId && accountId) {
    notification.onclick = () => {
      window.focus();
      window.location.href = getConversationURL(accountId, conversationId);
      notification.close();
    };
  }
  return true;
};
